import {
  convertRegistrationUf,
  RegistrationUf,
  ufAbbreviation,
} from '@/api/enumerations';
import { CoverageData } from '@/api/theHive/noticeCoverage/types';
import { normalizeText } from '@/helpers';
import { useCallback, useDeferredValue, useMemo, useState } from 'react';

import { UF_NAMES } from '../ufNames';

// notice-coverages retorna city.uf de forma inconsistente: um código numérico
// RegistrationUf em alguns payloads e a sigla ("SP") em outros. Resolve ambos
// na sigla de 2 letras usada para agrupar/exibir.
const resolveUf = (rawUf: number | string): string => {
  if (typeof rawUf === 'number') {
    return ufAbbreviation[rawUf as RegistrationUf] ?? '';
  }
  if (typeof rawUf === 'string' && rawUf.trim() !== '') {
    const numericUf = Number(rawUf);
    return Number.isNaN(numericUf)
      ? rawUf.toUpperCase()
      : ufAbbreviation[numericUf as RegistrationUf] ?? '';
  }
  return '';
};

export interface CoverageMicroregion {
  id: number;
  name: string;
}

export interface CoveragePole {
  id: number;
  name: string;
  uf: string;
  microregions: CoverageMicroregion[];
  microregionsCount: number;
  displacementFee: number | undefined;
}

export interface CoverageUfGroup {
  uf: string;
  poles: CoveragePole[];
  total: number;
  microregions: number;
}

export interface UseCoverageViewHook {
  search: string;
  setSearch: (value: string) => void;
  ufFilterValue: number;
  setUfFilterValue: (value: number) => void;
  clearFilters: () => void;
  isFilterActive: boolean;
  visibleGroups: CoverageUfGroup[];
}

export default function useCoverageView(
  coverageData: CoverageData[]
): UseCoverageViewHook {
  const [search, setSearch] = useState('');
  const [activeUf, setActiveUf] = useState<string | null>(null);

  const deferredSearch = useDeferredValue(search);

  const poles = useMemo<CoveragePole[]>(
    () =>
      coverageData.map((coverage) => {
        const microregions = (coverage.subregions ?? []).map((subregion) => ({
          id: subregion.id,
          name: subregion.city.name,
        }));
        return {
          id: coverage.id,
          name: coverage.name || coverage.city.name,
          uf: resolveUf(coverage.city.uf),
          microregions,
          microregionsCount: microregions.length,
          displacementFee: coverage.displacement_fee,
        };
      }),
    [coverageData]
  );

  // texto pesquisável pré-computado por polo: nome, UF, nome do estado, microrregiões
  const searchKeys = useMemo(() => {
    const keys = new Map<number, string>();
    poles.forEach((pole) => {
      const microregionNames = pole.microregions
        .map((microregion) => microregion.name)
        .join(' ');
      keys.set(
        pole.id,
        normalizeText(
          `${pole.name} ${pole.uf} ${
            UF_NAMES[pole.uf] ?? ''
          } ${microregionNames}`
        )
      );
    });
    return keys;
  }, [poles]);

  const normalizedSearch = useMemo(
    () => normalizeText(deferredSearch.trim()),
    [deferredSearch]
  );

  const visiblePoles = useMemo(
    () =>
      poles.filter((pole) => {
        if (activeUf && pole.uf !== activeUf) return false;
        if (normalizedSearch === '') return true;
        return (searchKeys.get(pole.id) ?? '').includes(normalizedSearch);
      }),
    [poles, activeUf, normalizedSearch, searchKeys]
  );

  const visibleGroups = useMemo(() => {
    const byUf = new Map<string, CoveragePole[]>();
    visiblePoles.forEach((pole) => {
      const group = byUf.get(pole.uf);
      if (group) {
        group.push(pole);
      } else {
        byUf.set(pole.uf, [pole]);
      }
    });
    return Array.from(byUf.entries())
      .map(([uf, groupPoles]) => ({
        uf,
        poles: groupPoles,
        total: groupPoles.length,
        microregions: groupPoles.reduce(
          (sum, pole) => sum + pole.microregionsCount,
          0
        ),
      }))
      .sort((a, b) => a.uf.localeCompare(b.uf));
  }, [visiblePoles]);

  const ufFilterValue = useMemo(() => {
    if (!activeUf) return 0;
    const enumValue = RegistrationUf[activeUf as keyof typeof RegistrationUf];
    return typeof enumValue === 'number' ? enumValue : 0;
  }, [activeUf]);

  const setUfFilterValue = useCallback((value: number): void => {
    setActiveUf(value === 0 ? null : convertRegistrationUf(value));
  }, []);

  const clearFilters = useCallback((): void => {
    setSearch('');
    setActiveUf(null);
  }, []);

  const isFilterActive = activeUf !== null || search !== '';

  return {
    search,
    setSearch,
    ufFilterValue,
    setUfFilterValue,
    clearFilters,
    isFilterActive,
    visibleGroups,
  };
}
